import styled from 'styled-components';
import { SvgSprite } from 'modules/svgSprite';
import { objectToPascalCase } from 'modules/helpers/strings';
import { breakpoint, sizes, colors, constants } from 'variables';
import { LinkButton, ButtonStyle, withIcon } from 'modules/react-button';
import type { OpeningHourTexts, StoreModel } from 'autogen/swagger/all';
import OpeningHoursDefault from 'components/OpeningHours';
import type { ConsultationProductListViewModel } from 'autogen/swagger/all';
import ConsultationProductItem from 'components/booking/consultations/consultationProductItem';
import {
    ColorNordicGrey4,
    Spacing12,
    Spacing16,
    Spacing20,
    Spacing24,
    Spacing32,
    Spacing64,
    Spacing8
} from 'autogen/design-tokens/tokens';
import ContainerFluid from 'components/Layout/components/ContainerFluid';
import ConsultationContentHeader from 'components/booking/consultations/ConsultationProductList/components/ConsultationContentHeader/ConsultationContentHeader';
import { useTranslation } from 'react-i18next';
import { StorePageViewAll } from 'autogen/translation-keys/trans-website-booking-consultationlist';
import type { LinkAction } from 'components/shared/Routing/components/LinkAction/shared/types';
import LinkActionButton from 'components/Button/LinkActionButton';

const Wrapper = styled.div`
    margin-top: 10px;
    display: flex;
    flex-direction: column;
    margin-bottom: ${Spacing20};

    ${breakpoint.up(sizes.md)} {
        padding: 30px;
        border: 1px solid ${colors.borderPrimary};
        border-radius: ${constants.borderRadius1};
        flex-direction: row;
        justify-content: space-between;
        margin-top: ${Spacing12};
        margin-bottom: ${Spacing64};

        flex-wrap: wrap;
    }

    ${breakpoint.up(sizes.lg)} {
        flex-wrap: none;
    }
`;

const Header = styled.h1`
    text-align: center;
    font-size: 18px;
    font-weight: bold;
    margin-bottom: 20px;
    margin-top: 10px;

    ${breakpoint.up(sizes.md)} {
        display: block;
        margin-top: 50px;
        margin-bottom: 20px;
        text-align: left;
        font-size: 32px;
    }
`;

const Left = styled.div`
    ${breakpoint.up(sizes.md)} {
        display: flex;
        flex: 1 1 auto;
        max-width: 50%;
        margin-bottom: 10px;
    }

    ${breakpoint.up(sizes.lg)} {
        margin-bottom: 0;
    }
`;

const Image = styled.img`
    height: auto;
    width: 100%;
    margin-bottom: ${Spacing16};

    ${breakpoint.up(sizes.md)} {
        display: none;
    }
`;

const ImageDesktop = styled.img`
    display: none;

    ${breakpoint.up(sizes.md)} {
        display: block;
        margin-right: 30px;
        width: auto;
        height: auto;
        max-height: 100%;
        max-width: 300px;
        flex: 1 1 auto;
        min-width: 1px;
    }
`;

const AddressWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    width: 100%;
    gap: ${Spacing20};

    ${breakpoint.up(sizes.md)} {
        flex-direction: column;
        width: auto;
    }
`;

const Address = styled.div`
    display: flex;
    flex-direction: column;
`;
const AddressLine = styled.span`
    &:first-of-type {
        font-weight: bold;

        ${breakpoint.up(sizes.md)} {
            margin-bottom: ${Spacing16};
            font-size: 15px;
        }
    }

    &:last-of-type {
        font-weight: bold;
        margin-top: ${Spacing16};
    }
`;

const FindWayLink = styled(LinkActionButton)`
    width: 110px;
    font-weight: bold;
    text-align: center;
`;

const OpeningHoursWrapper = styled.div`
    margin-top: 20px;
    flex: 0.5 1 auto;

    ${breakpoint.up(sizes.md)} {
        margin: 0 10px;
    }
`;

const OpeningHoursHeader = styled.div`
    font-weight: bold;

    ${breakpoint.up(sizes.md)} {
        font-size: 18px;
    }
`;

const OpeningHours = styled(OpeningHoursDefault)`
    margin-top: 10px;
`;

const FollowLinks = styled.div`
    margin: 20px 0;

    ${breakpoint.up(sizes.md)} {
        margin: 0;
    }
`;

const FacebookLink = styled(withIcon(LinkButton))`
    width: 100%;
    font-weight: bold;

    ${breakpoint.up(sizes.md)} {
        width: 275px;
    }
`;

const BackButton = styled.button`
    display: flex;
    background: none;
    outline: none;
    border: none;
    align-items: center;
    height: 30px;
    font-weight: bold;
    padding: 5px 0;
    margin-top: 10px;

    ${breakpoint.up(sizes.md)} {
        display: none;
    }
`;

const BackButtonIcon = styled(SvgSprite)`
    width: 15px;
    height: 15px;
    margin-right: 10px;
`;

const SectionBackground = styled.div`
    background-color: ${ColorNordicGrey4};
`;

const ConsultationProductItemWrapper = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: ${Spacing8};
    margin-bottom: ${Spacing16};

    ${breakpoint.up(sizes.md)} {
        grid-template-columns: repeat(3, 1fr);
        gap: ${Spacing24};
        margin-bottom: ${Spacing24};
    }

    ${breakpoint.up(sizes.lg)} {
        grid-template-columns: repeat(4, 1fr);
    }
`;

const StyledConsultationContentHeader = styled(ConsultationContentHeader)`
    margin-bottom: ${Spacing32};

    ${breakpoint.up(sizes.md)} {
        margin-bottom: ${Spacing64};
    }
`;

interface IStorePage {
    store: StoreModel;
    translations;
    findMyWay?: LinkAction;
    findStoreUrl?: LinkAction;
    consultationProducts: Pick<ConsultationProductListViewModel, 'hits'>;
    bookingFrontPage: LinkAction;
}

const StorePage = (props: IStorePage): JSX.Element => {
    const { store, translations, findMyWay, findStoreUrl, consultationProducts, bookingFrontPage } = props;
    const { address, city, imageUrl, name, openingHourTexts, zipCode, facebookUrl, phone } = store;
    const pascalTranslations = objectToPascalCase(translations);
    const { FollowOnFacebook, FindWay, OpeningHours: OpeningHoursHeadline, Back, Phone } = pascalTranslations;
    const { t } = useTranslation();

    const onBackClick = (): void => {
        if (document.referrer.indexOf(window.location.hostname) > -1) {
            window.history.back();
        } else {
            window.location.href = findStoreUrl.url;
        }
    };

    return (
        <>
            <ContainerFluid>
                <BackButton onClick={onBackClick}>
                    <BackButtonIcon sprite="ArrowLeft" />
                    {Back}
                </BackButton>
                <Header>{name}</Header>
                <Wrapper>
                    <Left>
                        {store.imageUrl && <ImageDesktop src={store.imageUrl} />}
                        <AddressWrapper>
                            <Address>
                                <AddressLine>{name}</AddressLine>
                                <AddressLine>{address}</AddressLine>
                                <AddressLine>
                                    {zipCode} {city}
                                </AddressLine>
                                <AddressLine>
                                    <a href={`tel:${(phone?.match(/\d+/g) || []).join('')}`} className="link">
                                        {Phone} {phone}
                                    </a>
                                </AddressLine>
                            </Address>
                            <FindWayLink action={findMyWay} target="_blank" variant="secondary">
                                {FindWay}
                            </FindWayLink>
                        </AddressWrapper>
                    </Left>
                    <OpeningHoursWrapper>
                        <OpeningHoursHeader>{OpeningHoursHeadline}</OpeningHoursHeader>
                        <OpeningHours openingHours={openingHourTexts} />
                    </OpeningHoursWrapper>
                    <FollowLinks>
                        {!!facebookUrl && (
                            <FacebookLink
                                target="_blank"
                                icon={{ iconType: 'SocialFacebook' }}
                                href={facebookUrl}
                                buttonStyle={ButtonStyle.secondary}
                            >
                                {FollowOnFacebook}
                            </FacebookLink>
                        )}
                    </FollowLinks>

                    <Image src={imageUrl} />
                </Wrapper>
            </ContainerFluid>
            {!!consultationProducts.hits.length && (
                <SectionBackground>
                    <StyledConsultationContentHeader />
                    <ContainerFluid>
                        <ConsultationProductItemWrapper>
                            {consultationProducts.hits.map((hit) => (
                                <ConsultationProductItem item={hit} key={hit.consultationProductId} />
                            ))}
                        </ConsultationProductItemWrapper>
                        <LinkActionButton variant="primary" action={bookingFrontPage}>
                            {t(StorePageViewAll)}
                        </LinkActionButton>
                    </ContainerFluid>
                </SectionBackground>
            )}
        </>
    );
};

export default StorePage;
